import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, TrendingUp } from "lucide-react";
import { useSimplify } from "./simplify-context";

const EASE = [0.22, 1, 0.36, 1];

export function MaskedLine({ children, delay, testId }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        data-testid={testId}
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function FlowCanvas() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let w, h, dpr;

    const streams = Array.from({ length: 26 }, (_, i) => ({
      x: Math.random(),
      lane: i / 26,
      speed: 0.0006 + Math.random() * 0.0011,
      size: 1 + Math.random() * 2,
      alpha: 0.25 + Math.random() * 0.5,
    }));

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const laneY = (lane, x) => {
      // lanes converge toward the vertical centre as x moves right — one source of truth
      const spread = (1 - x) * h * 0.9;
      return h / 2 + (lane - 0.5) * spread;
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);

      ctx.strokeStyle = "rgba(0, 34, 210, 0.08)";
      ctx.lineWidth = 1;
      for (let i = 0; i < 7; i++) {
        const lane = i / 6;
        ctx.beginPath();
        for (let px = 0; px <= 1.001; px += 0.05) {
          const y = laneY(lane, px);
          if (px === 0) ctx.moveTo(px * w, y);
          else ctx.lineTo(px * w, y);
        }
        ctx.stroke();
      }

      streams.forEach((s) => {
        s.x += s.speed;
        if (s.x > 1) s.x = 0;
        const y = laneY(s.lane, s.x);
        const convergeGlow = s.x * s.x;
        ctx.beginPath();
        ctx.arc(s.x * w, y, s.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 34, 210, ${s.alpha * (0.35 + convergeGlow)})`;
        ctx.fill();
      });

      // destination node
      ctx.beginPath();
      ctx.arc(w - 4, h / 2, 5, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(216, 155, 0, 0.95)";
      ctx.fill();

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      data-testid="hero-visual-canvas"
      className="w-full h-full"
      aria-hidden="true"
    />
  );
}

export default function Hero() {
  const { setOpen } = useSimplify();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const visualY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section
      id="top"
      ref={sectionRef}
      className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-[#F8FAFC] pt-28 pb-16"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 w-full grid grid-cols-12 gap-8 lg:gap-12 items-center">
        <motion.div style={{ y: textY }} className="col-span-12 lg:col-span-7 relative z-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-6"
          >
            Ocean Data · For construction &amp; professional services
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-display font-bold tracking-tight leading-[1.04] text-brand-navy text-4xl sm:text-5xl lg:text-6xl"
          >
            <MaskedLine delay={0.35} testId="hero-headline-line-1">More capacity.</MaskedLine>
            <MaskedLine delay={0.5} testId="hero-headline-line-2">
              <span className="text-brand-teal">Better work.</span>
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-supporting-text"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            Ocean Data gives every person the information they need to spend
            more time doing what they're best at. Less paperwork. Less
            repetition. Less chasing.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05, ease: EASE }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <button
              data-testid="hero-primary-cta"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-3 rounded-full bg-brand-navy text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-teal hover:-translate-y-0.5 shadow-[0_20px_40px_-15px_rgba(14,20,51,0.35)]"
            >
              Show me where I can simplify
              <ArrowRight size={18} />
            </button>
            <a
              data-testid="hero-secondary-cta"
              href="#flow"
              className="text-sm font-semibold text-brand-navy border-b-2 border-brand-teal/40 pb-1 hover:border-brand-teal transition-colors duration-300"
            >
              See the before &amp; after
            </a>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.25 }}
            className="mt-4 text-xs text-slate-400"
          >
            Free 15-minute business review. No commitment.
          </motion.p>
        </motion.div>

        <motion.div
          style={{ y: visualY }}
          className="col-span-12 lg:col-span-5 relative h-[320px] sm:h-[420px] lg:h-[520px]"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.6, ease: EASE }}
            className="absolute inset-0 rounded-3xl bg-white border border-slate-100 shadow-[0_20px_40px_-15px_rgba(14,20,51,0.08)] overflow-hidden"
          >
            <FlowCanvas />
            <div className="absolute top-5 left-5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
              Scattered steps → one source
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.3, ease: EASE }}
            className="od-drift absolute -bottom-4 -left-2 sm:left-6 lg:-left-10 z-10"
          >
            <div
              data-testid="hero-capacity-badge"
              className="flex items-center gap-3 rounded-2xl bg-brand-navy text-white px-5 py-4 shadow-[0_24px_48px_-16px_rgba(14,20,51,0.5)]"
            >
              <span className="w-9 h-9 rounded-full bg-brand-gold/25 text-brand-gold flex items-center justify-center">
                <TrendingUp size={17} />
              </span>
              <div>
                <div className="font-display font-bold text-lg leading-none">25–40%</div>
                <div className="text-[11px] text-slate-300 mt-1">less admin time · 647 implementations</div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
